'use client'
import type { UpdateCartWithMultipleProductParams } from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import { createContext, useMemo, useState } from 'react'
import { GetProductNearServicesAccessoriesQuery } from '@productsite-beta/src/graphql/generated/graphql-types'

export const AttachedSalesContext = createContext({
  list: [] as AttachedSalesProduct[],
  handleIsSelected: (_: string) => {},
  updateQuantity: (_: string, __: number) => {},
  productsToAddToCart: [] as UpdateCartWithMultipleProductParams[],
  mainProductQuantity: 1,
  setMainProductQuantity: (_: number) => {},
})

type AttachedSalesProduct = UpdateCartWithMultipleProductParams & { isSelected: boolean }

type Props = {
  children: React.ReactNode
  mainProductErpIdentifier: string
  attachedSalesProducts: GetProductNearServicesAccessoriesQuery['productNearServicesAccessories']['products']
}

type Product =
  GetProductNearServicesAccessoriesQuery['productNearServicesAccessories']['products'][0]
export default function AttachedSalesProvider({
  children,
  attachedSalesProducts,
  mainProductErpIdentifier,
}: Props) {
  const [mainProductQuantity, setMainProductQuantity] = useState(1)
  const [list, setList] = useState(() => {
    if (!attachedSalesProducts) return []
    return attachedSalesProducts.map(
      (item: Product): AttachedSalesProduct => ({
        mainProductErpIdentifier,
        productErpIdentifier: item.productErpIdentifier,
        quantity: 1,
        product: item,
        isSelected: false,
      })
    )
  })
  const filteredList = useMemo(
    () => list.filter((item: AttachedSalesProduct) => item.isSelected),
    [list]
  )
  const productsToAddToCart = useMemo(
    () =>
      filteredList.map((item: AttachedSalesProduct) => {
        const { isSelected, ...rest } = item
        return rest
      }),
    [filteredList]
  )

  function updateQuantity(id: string, quantity: number) {
    const newList = list.map((item: AttachedSalesProduct) => {
      if (item.productErpIdentifier === id) {
        item.quantity = quantity
      }
      return item
    })
    setList(newList)
  }

  function handleIsSelected(id: string) {
    const newList = list.map((item: AttachedSalesProduct) => {
      if (item.productErpIdentifier === id) {
        item.isSelected = !item.isSelected
      }
      return item
    })
    setList(newList)
  }

  return (
    <AttachedSalesContext.Provider
      value={{
        list,
        handleIsSelected,
        updateQuantity,
        productsToAddToCart,
        mainProductQuantity,
        setMainProductQuantity,
      }}>
      {children}
    </AttachedSalesContext.Provider>
  )
}
